import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { Permission } from '@/types/model';

interface ProtectedRouteProps {
  /** Children to render if user has access */
  children: ReactNode;
  
  /** Required permission to access the route */
  permission?: Permission;
  
  /** Array of permissions - user needs ANY of these */
  anyPermission?: Permission[];
  
  /** Array of permissions - user needs ALL of these */
  allPermissions?: Permission[];
  
  /** Required role to access the route */
  role?: string;
  
  /** Array of roles - user needs ANY of these */
  anyRole?: string[];
  
  /** If true, only admin/superadmin can access */
  adminOnly?: boolean;
  
  /** Redirect path when access is denied (default: '/') */
  redirectTo?: string;
  
  /** Custom unauthorized component */
  unauthorizedComponent?: ReactNode;
}

/**
 * Component that protects routes based on user permissions
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permission,
  anyPermission,
  allPermissions,
  role,
  anyRole,
  adminOnly = false,
  redirectTo = '/',
  unauthorizedComponent,
}) => {
  const location = useLocation();
  const { 
    user,
    hasPermission, 
    hasAnyPermission, 
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isAdmin 
  } = usePermissions();

  // If user is not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin-only access
  if (adminOnly && !isAdmin()) {
    if (unauthorizedComponent) {
      return <>{unauthorizedComponent}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Check single permission
  if (permission && !hasPermission(permission)) {
    if (unauthorizedComponent) {
      return <>{unauthorizedComponent}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Check any permissions
  if (anyPermission && anyPermission.length > 0 && !hasAnyPermission(anyPermission)) {
    if (unauthorizedComponent) {
      return <>{unauthorizedComponent}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Check all permissions
  if (allPermissions && allPermissions.length > 0 && !hasAllPermissions(allPermissions)) {
    if (unauthorizedComponent) {
      return <>{unauthorizedComponent}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Check single role
  if (role && !hasRole(role)) {
    if (unauthorizedComponent) {
      return <>{unauthorizedComponent}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // Check any roles
  if (anyRole && anyRole.length > 0 && !hasAnyRole(anyRole)) {
    if (unauthorizedComponent) {
      return <>{unauthorizedComponent}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // User has access, render children
  return <>{children}</>;
};

/**
 * Component for displaying unauthorized access message
 */
export const UnauthorizedPage: React.FC = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 text-center">
        <div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Access Denied
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            You don't have permission to access this page.
          </p>
        </div>
        <div>
          <button
            onClick={() => window.history.back()}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
};